// MatchGoalsManager.js
import { useState, useEffect } from "react";
import { showToast } from "../../ToastNotification";
import { useSelector } from "react-redux";

export const useMatchGoals = (matchId, match) => {
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const token = useSelector((state) => state.user.token);
  const [players, setPlayers] = useState({
    team1: [],
    team2: [],
    loading: false,
    error: null,
  });

  const fetchGoals = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/matches/${matchId}/goals`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (!response.ok) throw new Error("Error al obtener goles");
      const data = await response.json();
      setGoals(data);
    } catch (err) {
      setError(err.message);
      showToast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlayersByTeam = async (teamId, teamKey, categoryId = null) => {
    try {
      setPlayers((prev) => ({ ...prev, loading: true }));

      let url = `${process.env.REACT_APP_API_URL}/api/players/team/${teamId}`;
      if (categoryId != null) url += `?category=${categoryId}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) throw new Error("Error al obtener jugadores");

      const data = await response.json();

      setPlayers((prev) => ({
        ...prev,
        [teamKey]: data,
        loading: false,
      }));
    } catch (err) {
      setPlayers((prev) => ({
        ...prev,
        loading: false,
        error: err.message,
      }));
      showToast.error(err.message);
    }
  };

  const addGoal = async (goalData) => {
    try {
      const goalDataWithAssist = {
        ...goalData,
        assist_player_id: goalData.assist_player_id || null,
      };

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/goals`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(goalDataWithAssist),
        },
      );

      if (!response.ok) throw new Error("Error al agregar gol");

      const newGoal = await response.json();

      // importante: no agregar el gol manualmente
      await fetchGoals(); // fuerza recarga 100% consistente

      showToast.success("Gol agregado correctamente");
      return newGoal;
    } catch (err) {
      showToast.error(err.message);
      throw err;
    }
  };

  const updateGoal = async (goalId, goalData) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/goals/${goalId}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(goalData),
        },
      );
      if (!response.ok) throw new Error("Error al actualizar gol");
      const updatedGoal = await response.json();
      setGoals((prev) => prev.map((g) => (g.id === goalId ? updatedGoal : g)));
      showToast.success("Gol actualizado correctamente");
      return updatedGoal;
    } catch (err) {
      showToast.error(err.message);
      throw err;
    }
  };

  const deleteGoal = async (goalId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/goals/${goalId}`,
        {
          Authorization: `Bearer ${token}`,
          method: "DELETE",
        },
      );
      if (!response.ok) throw new Error("Error al eliminar gol");
      setGoals((prev) => prev.filter((g) => g.id !== goalId));
      showToast.success("Gol eliminado correctamente");
    } catch (err) {
      showToast.error(err.message);
      throw err;
    }
  };

  useEffect(() => {
    if (matchId) {
      fetchGoals();
    }
  }, [matchId]);

  return {
    goals,
    loading,
    error,
    players,
    fetchPlayersByTeam,
    addGoal,
    updateGoal,
    deleteGoal,
    refreshGoals: fetchGoals,
  };
};
